import React, { useEffect, useState } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router";
import { LegalPage } from "./pages/LegalPage";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { FocusTimerProvider } from "./focus/FocusTimerProvider";
import { applyAccent, readAccent } from "./theme/accents";
import { AppShell } from "./layout/AppShell";
import { AuthPage } from "./pages/AuthPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CoursesPage } from "./pages/CoursesPage";
import { CourseDetailPage } from "./pages/CourseDetailPage";
import { ClassReportPage } from "./pages/ClassReportPage";
import { LibraryPage } from "./pages/LibraryPage";
import { AssistantPage } from "./pages/AssistantPage";
import { PlannerPage } from "./pages/PlannerPage";
import { TasksPage } from "./pages/TasksPage";
import { FocusPage } from "./pages/FocusPage";
import { ProgressPage } from "./pages/ProgressPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AcademicPathPage } from "./pages/AcademicPathPage";
import { MorePage } from "./pages/MorePage";
import { NotFoundPage } from "./pages/NotFoundPage";

function BootSplash() {
  const { loading } = useAuth();
  const [minVisible, setMinVisible] = useState(true);
  useEffect(() => {
    const timer = window.setTimeout(() => setMinVisible(false), 1600);
    return () => window.clearTimeout(timer);
  }, []);
  const done = !loading && !minVisible;
  return (
    <div className={`boot-splash ${done ? "is-done" : ""}`} aria-hidden={done}>
      <img className="boot-splash__icon" src="/nelurio-icon.svg" alt="" width="96" height="96" />
      <strong>Nelurio</strong>
      <img className="boot-splash__spinner" src="/nelurio-loading.svg" alt="" width="44" height="44" />
    </div>
  );
}

function FullscreenLoading() {
  return (
    <div className="full-screen-loading" role="status">
      <img src="/nelurio-loading-light.svg" alt="" width="120" height="120" />
      <strong>Nelurio</strong>
    </div>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoading />;
  if (!user) return <Navigate to="/auth" replace />;
  return <AppShell />;
}

function GuestRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoading />;
  return user ? <Navigate to="/" replace /> : <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <GuestRoute />,
    children: [
      { path: "/auth", element: <AuthPage /> },
      { path: "/legal/imprint", element: <LegalPage page="imprint" /> },
      { path: "/legal/privacy", element: <LegalPage page="privacy" /> }
    ]
  },
  {
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "courses", element: <CoursesPage /> },
      { path: "courses/:courseId", element: <CourseDetailPage /> },
      { path: "reports/new", element: <ClassReportPage /> },
      { path: "library", element: <LibraryPage /> },
      { path: "assistant", element: <AssistantPage /> },
      { path: "planner", element: <PlannerPage /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "focus", element: <FocusPage /> },
      { path: "progress", element: <ProgressPage /> },
      { path: "academic-path", element: <AcademicPathPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "more", element: <MorePage /> },
      { path: "*", element: <NotFoundPage /> }
    ]
  }
]);

applyAccent(readAccent());

export function App() {
  return (
    <AuthProvider>
      <FocusTimerProvider>
        <BootSplash />
        <RouterProvider router={router} />
      </FocusTimerProvider>
    </AuthProvider>
  );
}
